import React, { useEffect, useMemo, useState } from "react";
import {
  Button, Card, Table, Input, Modal, Form, Tag, Space, Typography, Tooltip, Empty, Dropdown,
} from "antd";
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, MoreOutlined,
  BankOutlined, ReloadOutlined, ExclamationCircleFilled,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";

const { Title, Text } = Typography;
const SEARCH_KEY = "typeEtabV2_search";

/**
 * v2 de /autre-parametres/type-etablissements — écran AntD v5 autonome (l'ancien modal était en
 * React-Bootstrap). Types d'établissement = Parametre(typeParametre = TYPE_ETABLISSEMENT).
 * Ajout : recherche persistée, nombre d'établissements utilisant chaque type, garde à la suppression.
 */
const TypeEtablissementsV2 = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  // Recherche persistée (init paresseuse depuis sessionStorage).
  const [search, setSearch] = useState(() => sessionStorage.getItem(SEARCH_KEY) || "");
  // Nombre d'établissements par type (clé = parametreId en chaîne).
  const [usageByType, setUsageByType] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => { sessionStorage.setItem(SEARCH_KEY, search); }, [search]);

  const fetchTypes = () => {
    setLoading(true);
    axiosInstance
      .get(`/api/parametre/type/TYPE_ETABLISSEMENT`)
      .then((res) => setTypes((res.data || []).sort((a, b) => b.parametreId - a.parametreId)))
      .catch((err) => {
        console.error("Erreur chargement types d'établissement :", err);
        toast.error("Erreur lors du chargement des types d'établissement");
      })
      .finally(() => setLoading(false));
  };

  // Comptage des établissements par type (best-effort : l'établissement porte l'id du type).
  const fetchUsage = () => {
    axiosInstance
      .get(`/api/etablissement`)
      .then((res) => {
        const map = {};
        (res.data || []).forEach((e) => {
          const t = e?.typeEtablissement;
          const key = t != null && typeof t === "object" ? (t.parametreId ?? t.id) : t;
          if (key != null && `${key}`.trim() !== "") {
            map[`${key}`] = (map[`${key}`] || 0) + 1;
          }
        });
        setUsageByType(map);
      })
      .catch(() => setUsageByType({}));
  };

  useEffect(() => { fetchTypes(); fetchUsage(); }, []);

  const usageOf = (record) => usageByType[`${record.parametreId}`] || 0;

  const openAdd = () => {
    setEditMode(false); setCurrent(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditMode(true); setCurrent(record);
    form.setFieldsValue({ libelleParametre: record.libelleParametre });
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setCurrent(null); form.resetFields(); };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    const libelle = (values.libelleParametre || "").trim();
    if (!libelle) { toast.warning("Le libellé est obligatoire."); return; }

    const duplicate = types.some(
      (t) => (t.libelleParametre || "").toLowerCase() === libelle.toLowerCase()
        && (!editMode || t.parametreId !== current?.parametreId)
    );
    if (duplicate) { toast.error("Ce type d'établissement existe déjà."); return; }

    setSaving(true);
    const payload = { ...(current || {}), libelleParametre: libelle, typeParametre: "TYPE_ETABLISSEMENT" };
    const request = editMode ? axiosInstance.put(`/api/parametre`, payload) : axiosInstance.post(`/api/parametre`, payload);
    request
      .then((res) => {
        toast.success(editMode ? "Type modifié avec succès" : "Type ajouté avec succès");
        const saved = res.data;
        setTypes((prev) => {
          const exists = prev.some((t) => t.parametreId === saved?.parametreId);
          const next = exists
            ? prev.map((t) => (t.parametreId === saved.parametreId ? saved : t))
            : [saved, ...prev];
          return next.sort((a, b) => b.parametreId - a.parametreId);
        });
        closeModal();
      })
      .catch((err) => {
        console.error("Erreur enregistrement type d'établissement :", err);
        toast.error("Impossible d'enregistrer le type d'établissement.");
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (record) => {
    const nb = usageOf(record);
    Modal.confirm({
      title: "Supprimer ce type d'établissement ?",
      icon: <ExclamationCircleFilled />,
      content: nb > 0
        ? `Ce type est utilisé par ${nb} établissement(s). Le supprimer peut les laisser sans type. Continuer ?`
        : "Cette action est irréversible.",
      okText: "Supprimer", okType: "danger", cancelText: "Annuler",
      onOk: () => axiosInstance
        .delete(`/api/parametre/${record.parametreId}`)
        .then(() => {
          setTypes((prev) => prev.filter((t) => t.parametreId !== record.parametreId));
          toast.success("Type d'établissement supprimé.");
        })
        .catch((err) => {
          console.error("Erreur suppression :", err);
          toast.error("Impossible de supprimer ce type d'établissement.");
        }),
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return types;
    return types.filter((t) => (t.libelleParametre || "").toLowerCase().includes(q));
  }, [types, search]);

  const columns = [
    {
      title: "Libellé", dataIndex: "libelleParametre", key: "libelleParametre",
      sorter: (a, b) => (a.libelleParametre || "").localeCompare(b.libelleParametre || ""),
      render: (v) => <Text strong>{v || "—"}</Text>,
    },
    {
      title: "Établissements", key: "usage", width: 160, align: "center",
      sorter: (a, b) => usageOf(a) - usageOf(b),
      render: (_, r) => {
        const nb = usageOf(r);
        return nb > 0
          ? <Tag color="blue" icon={<BankOutlined />}>{nb}</Tag>
          : <Tag>0</Tag>;
      },
    },
    {
      title: "Actions", key: "actions", width: 90, align: "center",
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Modifier", icon: <EditOutlined /> },
              { key: "delete", label: "Supprimer", icon: <DeleteOutlined />, danger: true },
            ],
            onClick: ({ key }) => (key === "edit" ? openEdit(record) : handleDelete(record)),
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* En-tête */}
              <Card
                style={{ borderRadius: 14, marginBottom: 16, border: "none",
                  background: "linear-gradient(135deg, #242166 0%, #3b3a99 100%)" }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <Space align="center" size={14}>
                  <div style={{ background: "rgba(255,255,255,.15)", borderRadius: 12, padding: 12, display: "flex" }}>
                    <BankOutlined style={{ fontSize: 24, color: "#fff" }} />
                  </div>
                  <div>
                    <Title level={4} style={{ color: "#fff", margin: 0 }}>Types d'établissement</Title>
                    <Text style={{ color: "rgba(255,255,255,.8)" }}>
                      Catégories d'établissements payés sur sinistres (hôpital, garage, BNCB…) — {types.length} type(s)
                    </Text>
                  </div>
                </Space>
              </Card>

              {/* Barre d'outils */}
              <Card style={{ borderRadius: 14 }} bodyStyle={{ padding: 16 }}>
                <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }} wrap>
                  <Space wrap>
                    <Input
                      allowClear
                      prefix={<SearchOutlined />}
                      placeholder="Rechercher un libellé…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ width: 280 }}
                    />
                    <Tooltip title="Rafraîchir">
                      <Button icon={<ReloadOutlined />} onClick={() => { fetchTypes(); fetchUsage(); }} />
                    </Tooltip>
                  </Space>
                  <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                    Ajouter un type
                  </Button>
                </Space>

                <Table
                  rowKey="parametreId"
                  columns={columns}
                  dataSource={filtered}
                  loading={loading}
                  size="middle"
                  pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} type(s)` }}
                  locale={{ emptyText: <Empty description="Aucun type d'établissement" /> }}
                />
              </Card>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      {/* Modal ajout / édition (AntD) */}
      <Modal
        title={editMode ? "Modifier le type d'établissement" : "Ajouter un type d'établissement"}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Enregistrer"
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="libelleParametre"
            label="Libellé"
            rules={[{ required: true, message: "Le libellé est obligatoire." }]}
          >
            <Input placeholder="Ex : CLINIQUE, GARAGE, BNCB…" autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TypeEtablissementsV2;
